import { useState, useEffect } from 'react';
import { messaging, db } from '../lib/firebase';
import { getToken, onMessage } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { notify } from '../lib/toast';

export const usePushNotifications = (userId: string | undefined) => {
    const [permission, setPermission] = useState<NotificationPermission>('default');
    const [fcmToken, setFcmToken] = useState<string | null>(null);

    useEffect(() => {
        if ('Notification' in window) {
            setPermission(Notification.permission);
        }
    }, []);

    const requestPermission = async () => {
        if (!userId) return;

        try {
            if (!('Notification' in window)) {
                console.log('This browser does not support desktop notification');
                return;
            }

            const status = await Notification.requestPermission();
            setPermission(status);

            if (status === 'granted') {
                // Get Token
                if (!messaging) {
                    console.log('Firebase Messaging is not supported or not initialized.');
                    return;
                }

                const token = await getToken(messaging, {
                    vapidKey: 'BMc_tD9r_y... (You need a VAPID key from Firebase Console -> Project Settings -> Cloud Messaging)'
                });

                if (token) {
                    console.log('FCM Token:', token);
                    setFcmToken(token);

                    const userRef = doc(db, 'users', userId);
                    await updateDoc(userRef, {
                        fcm_web_tokens: arrayUnion(token),
                        last_fcm_update: Date.now()
                    });

                    notify.success('Bildirimler başarıyla açıldı!');
                }
            } else {
                notify.error('Bildirim izni reddedildi.');
            }
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            notify.error('Bildirim izni alınırken hata oluştu.');
        }
    };

    // Listen for foreground messages
    useEffect(() => {
        if (permission === 'granted' && messaging) {
            const unsubscribe = onMessage(messaging, (payload) => {
                console.log('Foreground Message received:', payload);
                if (payload.notification) {
                    notify.success(`🔔 ${payload.notification.title}: ${payload.notification.body}`);
                }
            });
            return () => unsubscribe();
        }
    }, [permission]);

    // NEW: Listen for Expo Native Bridge Token
    useEffect(() => {
        if (!userId) return;

        // Global callback for Native Bridge (More reliable than postMessage)
        // @ts-ignore
        window.registerExpoToken = async (token: string) => {
            console.log('🌉 Global Callback Received Expo Token:', token);
            if (!userId) return;
            const userRef = doc(db, 'users', userId);
            try {
                await updateDoc(userRef, {
                    expo_ios_token: token,
                    platform: 'ios_native',
                    last_fcm_update: Date.now()
                });
                setPermission('granted');
            } catch (err) {
                console.error('Failed to sync expo token:', err);
            }
        };

        const handleMessage = async (event: MessageEvent) => {
            try {
                // Parse message safely
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                if (data?.type === 'EXPO_PUSH_TOKEN' && data?.token) {
                    // @ts-ignore
                    window.registerExpoToken(data.token);
                }

                if (data?.type === 'NOTIFICATION_INTERACTION' && data?.url) {
                    console.log('📱 Notification Interaction Received:', data.url);
                    // navigate to the target URL
                    // Note: We need navigate from a hook or use window.location if necessary, 
                    // but since this is a hook we can pass navigate or use window.location.
                    if (data.url.startsWith('/')) {
                        window.location.hash = data.url; // Or better, use window.history.pushState if not using hash router
                        // Wait, looking at App.tsx we are using react-router-dom.
                        // We might need to expose navigate globally or use a custom event.
                        window.dispatchEvent(new CustomEvent('app-navigate', { detail: data.url }));
                    }
                }
            } catch (e) {
                // Ignore non-JSON messages
            }
        };

        // Android/iOS WebView communication
        window.addEventListener('message', handleMessage);

        // Request token from native wrapper if present
        const requestToken = () => {
            // @ts-ignore
            if (window.ReactNativeWebView) {
                console.log('📱 Requesting token from Native Wrapper...');
                // @ts-ignore
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'REQUEST_PUSH_TOKEN' }));
            }
        };

        // Initial request
        requestToken();

        // Also check if document has 'isNativeWrapper' (optional)
        // @ts-ignore
        if (window.isNativeWrapper) {
            console.log('📱 Running inside Native Wrapper');
            // Small delay to ensure bridge is ready
            setTimeout(requestToken, 1000);
            setTimeout(requestToken, 3000); // Retry once more later
        }

        return () => window.removeEventListener('message', handleMessage);
    }, [userId]);


    return {
        permission,
        requestPermission,
        fcmToken
    };
};
